<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Purchase Order" avatarImage="/glass/img/purchasing-overview-purchaseorder2.png" :avatarCircleMode="false" :loading="loading">
          <h4>{{poTitle}}</h4>

          <OverviewBoxInfoItem v-if="order.contact" icon="fa-industry">
            <LinkContact :id="order.contactId" :name="order.contact.name" />
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="order.order" icon="fa-shopping-cart">
            <LinkOrder :id="order.orderId" :name="order.order.sequenceNumber || 'Order'" />
          </OverviewBoxInfoItem>

          <OverviewBoxInfoItem icon="fa-map-marker-alt" v-if="isDespatchDelivery">
            <LinkGoogleMap
              :address1="order.deliveryAddress1"
              :address2="order.deliveryAddress2"
              :suburb="order.deliverySuburb"
              :postcode="order.deliveryPostcode"
              :state="order.deliveryState"
              :country="order.deliveryCountry"
              />
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle">
            {{order.totalPrice | formatCurrency}}
          </OverviewBoxInfoItem>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Type"><LabelBadge :labelClass="`label-po-type-${order.type}`">{{order.type | formatLookupName(PurchaseOrderTypeLookup)}}</LabelBadge></OverviewBoxListItem>
            <OverviewBoxListItem title="Status"><LabelBadge :labelClass="`label-po-status-${order.status}`">{{order.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge></OverviewBoxListItem>
            <OverviewBoxListItem title="Despatch">
              <LabelDespatchBadge :type="order.despatchType" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="order.sequenceNumber">{{order.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Location" v-if="order.accountLocation">{{order.accountLocation.name}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Container" v-if="isTypeBulk && order.stockContainer">{{order.stockContainer.name}}</OverviewBoxListItem>

            <OverviewBoxListItem title="Advised Shipping" v-if="isTypeBulk && order.dateAdvisedShipping">
              {{order.dateAdvisedShipping | formatDate}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Advised Receiving" v-if="isTypeBulk && order.dateAdvised">
              {{order.dateAdvised | formatDate}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Last Amended" v-if="order.isAmended">{{order.dateAmended | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created By">
              <LinkUser v-if="order.requestUser" :id="order.requestUserId" :name="order.requestUser.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{order.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{order.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="loading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-purchaseorder-summary">
              <i class="far fa-tasks"></i> Summary
            </Tab>
            <Tab tabid="tab-purchaseorder-details">
              <i class="far fa-clipboard-list-check"></i> Details
            </Tab>
            <Tab ref="receivingTab" tabid="tab-purchaseorder-receiving" v-if="isReceivingAvailable">
              <i class="far fa-dolly"></i> Stock Receiving
            </Tab>
            <Tab tabid="tab-purchaseorder-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-purchaseorder-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-purchaseorder-summary">
              <div class="row">
                <div class="col-lg-12" v-if="order.id">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderApproval) || userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder)" class="float-right">
                    <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder)" @click="copyPurchaseOrder" icon="fa-copy">Copy Purchase Order</DropdownMenuItem>
                    <template v-if="userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderApproval) && order.status == PurchaseOrderStatusTypes.pendingApproval">
                      <DropdownMenuItem @click="approvePurchaseOrder" icon="fa-thumbs-up">Approve &amp; Send</DropdownMenuItem>
                      <DropdownMenuItem v-if="order.isAmended" @click="approvePurchaseOrderAmendment" icon="fa-thumbs-up">Approve Amendment</DropdownMenuItem>
                      <DropdownMenuItem @click="denyPurchaseOrder" icon="fa-thumbs-down">Deny Purchase Order</DropdownMenuItem>
                    </template>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder) && isPurchaseOrderReSendable" @click="resendPurchaseOrder" icon="fa-envelope">Re-send Purchase Order</DropdownMenuItem>
                    <template v-if="userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder) && isApproved">
                      <DropdownMenuItem @click="displayPurchaseOrderReport()" icon="fa-print">Purchase Order Report</DropdownMenuItem>
                      <DropdownMenuItem @click="displayPurchaseOrderReport({ hidePrices: true })" icon="fa-print">Purchase Order Report (No pricing)</DropdownMenuItem>
                      
                      <DropdownMenuDivider v-if="canUpdateShippingDate || canUpdateReceivingDate || canUpdateReferences" />
                      <DropdownMenuItem @click="updateDateAdvisedShipping" icon="fa-clock" v-if="canUpdateShippingDate">Update Advised Shipping Date</DropdownMenuItem>
                      <DropdownMenuItem @click="updateDateAdvised" icon="fa-clock" v-if="canUpdateReceivingDate">Update Advised Receiving Date</DropdownMenuItem>
                      <DropdownMenuItem @click="editReferences" icon="fa-pencil" v-if="canUpdateReferences">Edit References</DropdownMenuItem>                      

                      <DropdownMenuDivider v-if="!isOrderCancelled && !isOrderComplete && !isOrderPendingPickup" />
                      <DropdownMenuItem @click="cancelPurchaseOrder" icon="fa-times" v-if="!isOrderCancelled && !isOrderComplete && !isOrderPendingPickup">Cancel Purchase Order</DropdownMenuItem>
                    </template>
                  </ActionDropMenu>

                  <ProgressTracker>
                    <ProgressTrackerItem :title="`${purchaseOrderTypeTitle} Purchase Order Created`" :completed="true" :cancelled="isOrderCancelled" icon="fa-list-alt" :timestamp="order.createdAt">
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-4">
                            <h5>CREATED BY</h5>
                            {{order.requestUser.name}}
                          </div>
                          <div class="col-lg-4">
                            <h5>SUPPLIER</h5>
                            {{order.contact.name}}
                          </div>
                          <div class="col-lg-4" v-if="isDespatchDelivery">
                            <h5>DELIVERING TO</h5>
                            <AddressDisplay
                              :address1="order.deliveryAddress1"
                              :address2="order.deliveryAddress2"
                              :suburb="order.deliverySuburb"
                              :postcode="order.deliveryPostcode"
                              :state="order.deliveryState"
                              :country="order.deliveryCountry"
                              />
                          </div>
                          <div class="col-lg-4" v-if="!isDespatchDelivery">
                            <h5>PICKUP</h5>
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelled && !isApproved)"
                      :title="isApproved ? 'Purchase Order Approved' : 'Waiting Approval'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingApproval"
                      :completed="isApproved"
                      :cancelled="isOrderCancelled"
                      icon="fa-thumbs-up"
                      :note="`Once approved, the purchase order will be assigned a reference number and emailed to ${order.contact.name}`"
                      :timestamp="order.approvalUpdatedDate">
                      <template v-slot:current>
                        <p>The purchase order is waiting for approval.</p>
                        <p>Once approved, the purchase order will be assigned a reference number and emailed to {{order.contact.name}}</p>
                        <br />
                        <ProgressTrackerButton @click="approvePurchaseOrder" icon="fa-thumbs-up">Approve &amp; Send</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <span v-if="order.isAmended">
                          <ProgressTrackerButton @click="approvePurchaseOrderAmendment" icon="fa-thumbs-up">Approve Amendment</ProgressTrackerButton>
                          &nbsp;&nbsp;
                        </span>

                        <ProgressTrackerButton @click="denyPurchaseOrder" icon="fa-thumbs-down">Deny Order</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-4">
                            <h5>APPROVED BY</h5>
                            {{order.statusUser.name}}
                          </div>
                          <div class="col-lg-4">
                            <h5>EMAILED TO</h5>
                            <template v-if="order.supplierEmailedList">
                              <p v-for="(emailed, index) in order.supplierEmailedList" :key="index">
                                {{emailed}}
                              </p>
                            </template>
                            <span v-else>-</span>
                          </div>
                          <div class="col-lg-4">
                            <ProgressTrackerButton @click="0" icon="fa-mail" v-if="false">View Communication</ProgressTrackerButton>
                          </div>
                        </div>
                      </template>
                      
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelled && !isApproved) && isTypeBulk"
                      :title="isStatusCompleted(PurchaseOrderStatusTypes.pendingShipping) ? 'Purchase Order Confirmed' : 'Waiting Supplier Confirmation'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingConfirmation"
                      :completed="isStatusCompleted(PurchaseOrderStatusTypes.pendingShipping)"
                      :cancelled="isOrderCancelled"
                      icon="fa-list-alt"
                      :timestamp="order.supplierConfirmDate"
                      note="Waiting for confirmation details once the supplier has received the purchase order">
                      <template v-slot:current>
                        <p>The purchase order is waiting confirmation the supplier has received it.</p>
                        <p>Once confirmed, the purchase order will be assigned a confirmation reference number.</p>
                        <br />
                        <ProgressTrackerButton @click="updateSupplierConfirmation" icon="fa-clipboard-check">Supplier Confirmed</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-2">
                            <h5>CONFIRMATION REFERENCE</h5>
                            {{order.supplierConfirmRef}}
                          </div>
                          <div class="col-lg-5">
                            <h5>ADVISED SHIPPING DATE</h5>
                            <template v-if="order.advisedShippingLog">
                              <p v-for="(recvInfo, index) in order.advisedShippingLog" :key="index" :class="[index==0 ? 'font-weight-bold' : '']">
                                {{recvInfo.dateAdvised | formatDate}}<span v-if="recvInfo.note"> - {{recvInfo.note}}</span>
                              </p>
                            </template>
                            <span v-else>TBA</span>
                          </div>
                          <div class="col-lg-5">
                            <h5>ADVISED RECEIVING DATE</h5>
                            <template v-if="order.advisedReceivingLog">
                              <p v-for="(recvInfo, index) in order.advisedReceivingLog" :key="index" :class="[index==0 ? 'font-weight-bold' : '']">
                                {{recvInfo.dateAdvised | formatDate}}<span v-if="recvInfo.note"> - {{recvInfo.note}}</span>
                              </p>
                            </template>
                            <span v-else>TBA</span>
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isTypeBulk"
                      :title="isStatusCompleted(PurchaseOrderStatusTypes.pendingReceival) ? 'Purchase Order Shipped' : 'Waiting Supplier Shipping'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingShipping"
                      :completed="isStatusCompleted(PurchaseOrderStatusTypes.pendingReceival)"
                      :cancelled="isOrderCancelled"
                      icon="fa-truck-container"
                      :timestamp="order.supplierShipDate"
                      note="Waiting for shipping tracking details once the supplier has sent the purchase order">
                      <template v-slot:current>
                        <p>The purchase order is waiting confirmation the supplier has shipped the items.</p>
                        <p>Once confirmed, the purchase order will be assigned a shipping reference number and an advised receiving date.</p>
                        <br />
                        <ProgressTrackerButton @click="updateSupplierShipped" icon="fa-truck">Supplier Shipped</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-4">
                            <h5>SHIPPING REFERENCE</h5>
                            {{order.supplierShipContainerRef}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isTypeBulk"
                      :title="isStatusCompleted(PurchaseOrderStatusTypes.receiving) ? 'Shipment Received' : 'Waiting Shipment Received'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingReceival"
                      :completed="isStatusCompleted(PurchaseOrderStatusTypes.receiving)"
                      :cancelled="isOrderCancelled"
                      icon="fa-container-storage"
                      :timestamp="order.dateReceived"
                      note="Waiting for shipment to arrive">
                      <template v-slot:current>
                        <p>The purchase order is waiting for the shipment to arrive.</p>
                        <p>When the shipment arrives, the items can be processed and added to the inventory</p>
                        <br />
                        <ProgressTrackerButton @click="updateDateAdvised" icon="fa-clock">Update Advised Receiving Date</ProgressTrackerButton>
                        <ProgressTrackerButton @click="updateShipmentReceived" icon="fa-container-storage" class="ml-2">Shipment Received</ProgressTrackerButton>
                      </template>
                    </ProgressTrackerItem>

                    <!-- Pending Receival - Delivery -->
                    <ProgressTrackerItem
                      v-if="!isOrderCancelled && (isTypeStandard || isTypeExternalOrder) && isDespatchDelivery"
                      :title="(order.status == PurchaseOrderStatusTypes.receiving || order.status == PurchaseOrderStatusTypes.completed) ? 'Goods Delivered' : 'Waiting Goods Delivered'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingReceival"
                      :completed="(order.status == PurchaseOrderStatusTypes.receiving || order.status == PurchaseOrderStatusTypes.completed || isOrderPendingPickup)"
                      :cancelled="isOrderCancelled"
                      icon="fa-truck"
                      :timestamp="order.dateReceived"
                      note="Waiting for goods to arrive">
                      <template v-slot:current>
                        <p>The purchase order is waiting for the goods to arrive.</p>
                        <p>When the goods arrives, the items can be processed and added to the inventory or external orders</p>
                        <br />
                        <ProgressTrackerButton @click="updateShipmentReceived" icon="fa-truck" class="ml-2">Goods Delivered</ProgressTrackerButton>
                      </template>
                    </ProgressTrackerItem>

                    <!-- Pending Available - Pickup -->
                    <ProgressTrackerItem
                      v-if="!isOrderCancelled && (isTypeStandard || isTypeExternalOrder) && isDespatchPickup"
                      :title="(isOrderPendingPickup || order.status == PurchaseOrderStatusTypes.completed) ? 'Goods Ready' : 'Waiting for goods to be available'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingAvailable"
                      :completed="(order.status == PurchaseOrderStatusTypes.completed || isOrderPendingPickup || isOrderInReceiving)"
                      :cancelled="isOrderCancelled"
                      icon="fa-truck"
                      :timestamp="order.dateReceived"
                      note="Waiting for goods to complete production or stock available and ready for pickup">
                      <template v-slot:current>
                        <p>The purchase order is waiting for the supplier to notify that goods are ready for pickup.</p>
                        <p>Once available, the goods will be ready for pickup</p>
                        <br />
                        <ProgressTrackerButton @click="updateReadyForPickup" icon="fa-truck" class="ml-2">Goods Ready</ProgressTrackerButton>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!isOrderCancelled && !isDenied && isDespatchPickup"
                      :title="isOrderComplete ? 'Goods Picked Up' : 'Waiting for pickup'"
                      :current="order.status == PurchaseOrderStatusTypes.pendingPickup"
                      :completed="isOrderComplete || isOrderInReceiving"
                      :cancelled="isOrderCancelled"
                      icon="fa-dolly"
                      :timestamp="order.dateClosed"
                      note="Waiting for goods to be picked up">
                      <template v-slot:all>
                        <PurchaseOrderPickupTaskList
                          ref="poPickupTaskList"
                          :purchaseOrder="order"
                          :canCreateTask="canAddPickupTask"
                          :canMoveToReceiving="canMoveToReceiving"
                          @goodsReceived="updateGoodsReceived"
                          @taskCreated="reloadData()" 
                        />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!isOrderCancelled && !isDenied"
                      :title="isOrderComplete ? 'Goods Received & Processed' : 'Waiting for goods to be received'"
                      :current="order.status == PurchaseOrderStatusTypes.receiving"
                      :completed="isOrderComplete"
                      :cancelled="isOrderCancelled"
                      icon="fa-dolly"
                      :timestamp="order.dateClosed"
                      note="Waiting for goods to be received & processed into the system">
                      <template v-slot:current>
                        <p>The purchase order is waiting for the received items to be processed.</p>
                        <p>Once processed, the items will be registered in the inventory system</p>
                        <br />
                        <ProgressTrackerButton @click="displayReceivingTab" icon="fa-dolly">Receiving</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-4">
                            <h5>RECEIVED NOTES</h5>
                            {{order.goodsReceivedNotes}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    

                    <ProgressTrackerItem
                      v-if="!isOrderCancelled && !isDenied"
                      title="Completed"
                      :completed="isOrderComplete"
                      :cancelled="isOrderCancelled"
                      icon="fa-check"
                      :timestamp="order.dateClosed"
                      note="Waiting for the purchase order to be completed">
                      <template v-slot:completed>
                        Completed
                      </template>
                    </ProgressTrackerItem>


                    <ProgressTrackerItem
                      v-if="isOrderCancelled && !isDenied"
                      title="Purchase Order Cancelled"
                      :cancelled="true"
                      :timestamp="order.dateClosed">
                      <template v-slot:cancelled>
                        <div class="row">
                          <div class="col-lg-8">
                            <h5>CANCELLATION REASON</h5>
                            {{order.cancelledReason}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isOrderCancelled && isDenied"
                      title="Purchase Order Denied"
                      :cancelled="true"
                      :timestamp="order.dateClosed">
                      <template v-slot:cancelled>
                        <div class="row">
                          <div class="col-lg-4">
                            <h5>DENIED BY</h5>
                            {{order.statusUser.name}}
                          </div>
                          <div class="col-lg-8">
                            <h5>DENIED REASON</h5>
                            {{order.deniedReason}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                  </ProgressTracker>

                </div>
              </div>
              
            </TabPage>

            <TabPage tabid="tab-purchaseorder-details">
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder) && isOrderEditable && !itemList.editing" class="float-right">
                    <DropdownMenuItem @click="editPurchaseOrder" icon="fa-pencil">Edit Purchase Order</DropdownMenuItem>
                    <DropdownMenuItem @click="editOrderItemList" icon="fa-list">Edit Order Items</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Supplier" v-if="order.contact">{{order.contact.name}}</DataItem>
                  <DataItem title="Supplier Address" v-if="order.contactAddress">{{order.contactAddress.formattedAddress}}</DataItem>
                  <DataItem title="Date Required">{{order.dateRequired | formatDate}}</DataItem>
                  <DataItem title="Date Advised">{{order.dateAdvised | formatDate}}</DataItem>
                  
                  <DataItem title="Delivery Address" v-if="isDespatchDelivery">
                    <AddressDisplay
                      :address1="order.deliveryAddress1"
                      :address2="order.deliveryAddress2"
                      :suburb="order.deliverySuburb"
                      :postcode="order.deliveryPostcode"
                      :state="order.deliveryState"
                      :country="order.deliveryCountry"
                      />
                  </DataItem>
                  <DataItem title="Notes / Instructions"><span v-html="convertNewlinesToBreaks(order.notes)" /></DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-3" v-if="isTypeBulk">
                <div class="col-lg-12">
                  <h3>Bulk Items</h3>

                  <FormGroup :form="itemList.form">

                    <BulkOrderItemList
                      ref="bulkEntryList"
                      v-model="order.purchaseOrderItems"
                      :editing="itemList.editing"
                      :contactId="order.contactId"
                      :stockContainerId="order.stockContainerId"
                      :currencyCode="supplierCurrencyCode"
                      @overrideUpdated="itemList.overrideWarnings = $event"
                    />

                    <FormSubmit v-if="itemList.editing" label="Update" :submitting="itemList.submitting" @submitted="submitItemList" @cancelled="cancelEditingItemList" />
                  </FormGroup>
                </div>
              </div>

              <div class="row mt-3" v-if="isTypeStandard || isTypeExternalOrder">
                <div class="col-lg-12">
                  <h3>Processed Items</h3>

                  <FormGroup :form="itemList.form">

                    <OrderItemList
                      ref="processedEntryList"
                      v-model="order.purchaseOrderItems"
                      :editing="itemList.editing"
                      :contactId="order.contactId"
                      :showTotals="true"
                      :priceType="PriceTypes.supplier"
                      :optionDiscountAvailable="false"
                      :optionCustomerApprovalAvailable="false"
                      :optionCustomFieldsAvailable="false"
                      :optionExternalAvailable="false"
                    />

                    <FormSubmit v-if="itemList.editing" label="Update" :submitting="itemList.submitting" @submitted="submitItemList" @cancelled="cancelEditingItemList" />
                  </FormGroup>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-purchaseorder-receiving">
              <FormGroup :form="recvItemForm">
                <div class="row">
                  <div class="col-lg-12">
                    <ActionDropMenu class="float-right" v-if="!editingReceiving">
                      <DropdownMenuItem v-if="isReceivingEditable" @click="beginReceiveEditing" icon="fa-pencil">Edit Goods Received</DropdownMenuItem>
                      <DropdownMenuItem v-if="isReceivingEditable && isTypeBulk" @click="displayReceivingSheet" icon="fa-print">Receiving Sheet</DropdownMenuItem>
                      <DropdownMenuItem v-if="(isOrderComplete || isOrderPendingPickup) && (isTypeBulk || isTypeStandard)" @click="displayReceivingLabels(null)" icon="fa-print">Received Goods Labels</DropdownMenuItem>
                      <DropdownMenuItem v-if="(isOrderComplete || isOrderPendingPickup) && isTypeExternalOrder && hasProductionLabels" @click="displayProductionPanelLabels(null)" icon="fa-print">Production Panel Labels</DropdownMenuItem>

                      <template v-if="isReceivingEditable">
                        <DropdownMenuDivider />
                        <DropdownMenuItem @click="completeReceiveGoods" icon="fa-check-circle">Complete Goods Receival</DropdownMenuItem>
                      </template>
                    </ActionDropMenu>

                    <h3>Stock Receiving</h3>
                    
                    <ReceivingItemList
                      :editingReceiving="editingReceiving"
                      v-model="recvItemList"
                      :order="order"
                      :totalAdditionalCost="calculateRecvTotalAdditionalCosts()"
                      :stockLookupList="stockLookupList"
                      @displayReceivingLabels="displayReceivingLabels"
                      @displayProductionPanelLabels="displayProductionPanelLabels"
                      >
                    </ReceivingItemList>

                  </div>
                </div>
                <div class="row mt-4">
                  <div class="col-lg-6">
                    <h3>Additional Costs</h3>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th width="40%">Type</th>
                          <th class="text-right">Amount</th>
                          <th width="0%"> </th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="(costInfo, index) of order.additionalCosts" :key="index">
                          <td>
                            <FormSelect :optionsPromise="addCostListPromise" v-model="costInfo.name" v-if="editingReceiving" />
                            <span v-if="!editingReceiving">{{costInfo.name}}</span>
                          </td>
                          <td>
                            <FormInputCurrency v-model="costInfo.cost" v-if="editingReceiving" />
                            <div class="text-right" v-if="!editingReceiving">{{costInfo.cost | formatDecimal}}</div>
                          </td>
                          <td>
                            <ActionDropMenu :tableAction="true" v-if="editingReceiving">
                              <DropdownMenuItem @click="recvRemoveAdditionalCost(index)" icon="fa-trash-alt">Remove</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                        <tr class="static-datatable-row-nostripe border-top">
                          <td>
                            <span class="float-right font-weight-bold">Total:</span>
                            <a class="btn btn-white btn-xs" @click.prevent="recvNewAdditionalCost" v-if="editingReceiving"><i class="far fa-plus"></i> Add Cost</a>                          
                          </td>
                          <td class="text-right font-weight-bold">{{calculateRecvTotalAdditionalCosts() | formatCurrency}}</td>
                          <td> </td>
                        </tr>
                      </template>
                    </StaticDataTable>
                  </div>
                  <div class="col-lg-6" v-if="isOrderComplete">
                    <h3>Goods Received Notes</h3>
                    <p>{{order.goodsReceivedNotes}}</p>
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-lg-12">
                    <FormSubmit v-if="editingReceiving" label="Update" :submitting="submittingReceiving" @submitted="submitUpdateGoodsReceived" @cancelled="cancelReceiveEditing" />
                  </div>
                </div>

              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-purchaseorder-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.purchaseOrder"
                    :linkId="order.id"
                    :permissionResource="PermissionTypes.PurchasingPermissionTypes.purchaseOrder"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-purchaseorder-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.purchaseOrder" :linkId="order.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>


<script>
import _ from 'lodash'
import currency from 'currency.js'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import detectUnload from '@/mixins/detectUnload'

import StaticDataTable from '@/components/table/staticDataTable'
import ProgressTracker from '@/components/layout/progressTracker.vue'
import ProgressTrackerItem from '@/components/layout/progressTrackerItem.vue'
import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import DocumentList from '@/components/documentList.vue'
import BulkOrderItemList from '@/modules/purchasing/components/bulkOrderItemList'
import OrderItemList from '@/modules/orders/components/orderItemList'
import ReceivingItemList from '@/modules/purchasing/components/receivingItemList'
import PurchaseOrderPickupTaskList from '@/modules/purchasing/components/purchaseOrderPickupTaskList'

import ApprovePurchaseOrderDialog from '@/modules/purchasing/views/approvePurchaseOrderDialog'
import DenyPurchaseOrderDialog from '@/modules/purchasing/views/denyPurchaseOrderDialog'
import ResendPurchaseOrderDialog from '@/modules/purchasing/views/resendPurchaseOrderDialog'
import SupplierConfirmationDialog from '@/modules/purchasing/views/supplierConfirmationDialog'
import SupplierShippedDialog from '@/modules/purchasing/views/supplierShippedDialog'
import DateAdvisedDialog from '@/modules/purchasing/views/dateAdvisedDialog'
import EditPurchaseOrderDialog from '@/modules/purchasing/views/editPurchaseOrderDialog'
import EditPurchaseOrderReferenceDialog from '@/modules/purchasing/views/editPurchaseOrderReferenceDialog'
import GoodsReceivedDialog from '@/modules/purchasing/views/goodsReceivedDialog'
import PurchaseOrderActionDialog from '@/modules/purchasing/views/purchaseOrderActionDialog'

import { LinkTypes, PriceTypes, StockPriceTypes, ApprovalTypes, PricingRequestStatusTypes, InventoryStockTypes, InventoryStockTypeLookup } from '@/store/types'
import { PurchaseOrderTypes, PurchaseOrderTypeLookup, PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup, DespatchTypes, DescriptorLookupTypes } from '@/store/types'


export default {
  name: "PurchaseOrder",
  mixins: [form, pdfMixin, orderItemMixin, detectUnload],
  components: {
    StaticDataTable,
    ProgressTracker,
    ProgressTrackerItem,
    ProgressTrackerButton,
    DocumentList,
    BulkOrderItemList,
    OrderItemList,
    ReceivingItemList,
    PurchaseOrderPickupTaskList
  },
  data() {
    return {
      loading: false,
      order: {},
      poActivityLog: [],
      editingReceiving: false,
      itemList: {
        form: {},
        overrideWarnings: false,
        editing: false,
        submitting: false
      },
      stockList: [],
      stockLookupList: [],
      recvItemList: [],
      submittingReceiving: false,
      recvItemForm: {},

      addCostListPromise: this.getAddCostListPromise(),
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.PurchaseOrderStatusTypes = PurchaseOrderStatusTypes
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
    this.PriceTypes = PriceTypes
  },
  mounted() {

    // Load the purchase order
    this.requestData()
  },
  beforeRouteLeave (to, from, next) {
    if(!this.editingReceiving)
      return next(true)
    
    this.confirmMessage(`Cancel Receiving?`, `You have unsaved changes with the receiving process, are you sure you want to leave?`)
    .then(success => {
      next(success)
    })
    .catch(function () {
      next(false)
    })
  },
  computed: {
    isTypeBulk() {
      return this.order.type == PurchaseOrderTypes.bulk
    },
    isTypeStandard() {
      return this.order.type == PurchaseOrderTypes.standard
    },
    isTypeExternalOrder() {
      return this.order.type == PurchaseOrderTypes.externalOrder
    },
    purchaseOrderTypeTitle() {
      return Utils.getLookupName(PurchaseOrderTypeLookup, this.order.type)
    },
    isDespatchDelivery() {
      return this.order.despatchType == DespatchTypes.delivery
    },
    isDespatchPickup() {
      return this.order.despatchType == DespatchTypes.pickup
    },
    isOrderEditable() {
      // If the order is cancelled, complete or in receiving, then it cannot be edited
      if(this.order.status == PurchaseOrderStatusTypes.completed || this.order.status == PurchaseOrderStatusTypes.cancelled)
        return false
      if(this.order.status == PurchaseOrderStatusTypes.receiving || this.order.status == PurchaseOrderStatusTypes.pendingPickup)
        return false

      return true
    },
    isReceivingEditable() {
      return this.order.status == PurchaseOrderStatusTypes.receiving
    },
    isOrderComplete() {
      return this.order.status == PurchaseOrderStatusTypes.completed
    },
    isOrderCancelled() {
      return this.order.status == PurchaseOrderStatusTypes.cancelled
    },
    isOrderPendingPickup() {
      return this.order.status == PurchaseOrderStatusTypes.pendingPickup
    },
    isOrderInReceiving() {
      return this.order.status == PurchaseOrderStatusTypes.receiving
    },
    isDenied() {
      return this.order.approvalStatus == ApprovalTypes.denied
    },
    isApproved() {
      return this.order.approvalStatus == ApprovalTypes.approved
    },
    isReceivingAvailable() {
      let receivingStatuses = [
        PurchaseOrderStatusTypes.receiving,
        PurchaseOrderStatusTypes.completed,
        PurchaseOrderStatusTypes.cancelled
      ]
      return _.indexOf(receivingStatuses, this.order.status) != -1
    },
    canUpdateShippingDate() {
      if(!this.isTypeBulk)
        return false
      if(this.isOrderComplete || this.isOrderCancelled)
        return false
      if(!this.isApproved)
        return false
      if(this.isStatusCompleted(PurchaseOrderStatusTypes.pendingReceival))
        return false
      return true
    },
    canUpdateReceivingDate() {
      if(!this.isTypeBulk)
        return false
      if(this.isOrderComplete || this.isOrderCancelled)
        return false
      if(!this.isApproved)
        return false
      if(this.isStatusCompleted(PurchaseOrderStatusTypes.receiving))
        return false
      return true
    },
    canUpdateReferences() {
      if(!this.isTypeBulk)
        return false
      if(this.isOrderComplete || this.isOrderCancelled)
        return false
      if(!this.isApproved)
        return false
      if(this.isStatusCompleted(PurchaseOrderStatusTypes.receiving))
        return false
      return true
    },
    isPurchaseOrderReSendable() {
      if(!this.isApproved)
        return false

      // Can only if approved & not completed
      // Allow to send if cancelled as notification to cancel
      if(this.order.status == PurchaseOrderStatusTypes.completed)
        return false
        
      return true
    },
    canMoveToReceiving() {
      return (this.order.status == PurchaseOrderStatusTypes.pendingPickup || this.order.status == PurchaseOrderStatusTypes.pendingReceival)
    },
    canAddPickupTask() {
      if(!this.userHasCreatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false

      // Canot only create if a pickup & in pending pickup status
      return this.isDespatchPickup && this.order.status == PurchaseOrderStatusTypes.pendingPickup
    },
    hasProductionLabels() {
      if(!this.recvItemList)
        return false
      return this.recvItemList.filter(recvItem => recvItem.productionPanelId != null).length > 0
    },
    poTitle() {
      var title = 'Purchase Order'
      if(this.order.sequenceNumber != null)
        title += ` #${this.order.sequenceNumber}`
      return title
    },
    taxName() {
      if(this.order.contact == null || this.order.contact.supplierTaxRate == null)
        return ''
      return this.order.contact.supplierTaxRate.displayName
    },
    supplierCurrencyCode() {
      if(this.order.contact == null)
        return ''
      return this.order.contact.supplierCurrencyCode
    },
    activityStreamURL() {
      return `/purchasing/purchaseorders/${this.$route.params.id}/activitystream`
    }
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()

      if(this.$refs.poPickupTaskList)
        this.$refs.poPickupTaskList.reloadData()

      this.requestData()
    },
    requestData() {
      this.loading = true

      Promise.all([
        this.$apiService.purchasingService.getPurchaseOrder(this.$route.params.id),
        this.$apiService.inventoryService.getStockList()
      ])
      .then(results => {
        this.order = results[0].data
        this.recvItemList = []

        // Create a copy of the received items list for adjustments
        if(this.order.purchaseOrderReceivedItems) {
          this.recvItemList = this.order.purchaseOrderReceivedItems.map(item => {
            var recvItem = _.cloneDeep(item)

            recvItem.isAdjustment = false

            return recvItem
          })
        }

        // Copy the order item list & modify for display/editing
        this.order.purchaseOrderItems = this.buildOrderItemList(this.order.purchaseOrderItems)

        this.poActivityLog = this.order.activityList

        // Allocate 3 slots for additional costs
        if(!_.isArray(this.order.additionalCosts))
          this.order.additionalCosts = []


        this.$data.stockList = results[1].data
        this.$data.stockLookupList = this.$data.stockList
          .filter(stock => stock.isActive && stock.type == InventoryStockTypes.glass)
          .map(stock => {
            return {
              value: stock.id,
              name: `${stock.code} | ${stock.name}`,
            }
          })
      })
      .finally(() => {
        this.loading = false
      })
    },

    isStatusCompleted(status) {
      // Must be in order
      let statusList = [
        PurchaseOrderStatusTypes.pendingApproval,
        PurchaseOrderStatusTypes.pendingConfirmation,   // Bulk Only
        PurchaseOrderStatusTypes.pendingShipping,       // Bulk Only
        PurchaseOrderStatusTypes.pendingReceival,
        PurchaseOrderStatusTypes.receiving,
        PurchaseOrderStatusTypes.completed,
        PurchaseOrderStatusTypes.cancelled
      ]

      return statusList.indexOf(this.order.status) >= statusList.indexOf(status)
    },

    // Copy the purchase order into a new purchase order
    copyPurchaseOrder() {
      this.$router.push({
        name: 'PurchaseOrderNew',
        params: {
          copyFromPO: true,
          sourcePurchaseOrderId: parseInt(this.$route.params.id)
        }
      })
    },

    approvePurchaseOrder() {
      this.displayModal(ApprovePurchaseOrderDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
        supplierContactId: this.order.contactId
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Approved')
          this.reloadData()
        }
      })
    },
    approvePurchaseOrderAmendment() {
      this.confirmMessage('Approve amendments?', 'Approve the purchase order amendments? This will move the purchase order back to the previous state prior to amendments')
      .then(success => {
        if(success) {
          this.$apiService.purchasingService.approvePurchaseOrderAmendment(parseInt(this.$route.params.id)).then(response => {
            this.successToast('Purchase Order Approved')
            this.reloadData()
          })
        }
      })
    },
    denyPurchaseOrder() {
      this.displayModal(DenyPurchaseOrderDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id)
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Denied')
          this.reloadData()
        }
      })
    },
    resendPurchaseOrder() {
      this.displayModal(ResendPurchaseOrderDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
        supplierContactId: this.order.contactId
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Resent')
          this.reloadData()
        }
      })
    },
    cancelPurchaseOrder() {
      this.displayModal(DenyPurchaseOrderDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
        isDenyMode: false
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Cancelled')
          this.reloadData()
        }
      })
    },
    editPurchaseOrder() {
      this.displayModal(EditPurchaseOrderDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
      }, this.order)
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Updated')
          this.reloadData()
        }
      })
    },
    editOrderItemList() {
      // If an external purchase order, give a warning about editing linked items
      if(this.isTypeExternalOrder) {
        let messageHTML = '<strong>Warning:</strong> This is an external purchase order that has order items linked directly to an order.<br /><br />'
        messageHTML += 'Editing the items may cause issues with completing the external items in the order itself.<br /><br />'
        messageHTML += 'It is recommended to edit the order itself to regenerate the purchase orders.<br /><br />Are you sure you want to edit the purchase order items?'

        this.confirmMessage('Edit Items?', '', { html: messageHTML })
        .then(success => {
          if(success)
            this.itemList.editing = true
        })
      } else {
        this.itemList.editing = true
      }
    },
    submitItemList() {
      this.itemList.submitting = true
      this.itemList.form = this.clearFormResponse()

      var updateData = {
        overrideWarnings: this.itemList.overrideWarnings
      }
      if(this.isTypeBulk) {
        updateData.bulkItemList = this.order.purchaseOrderItems
      } else {
        updateData.processedItemList = this.prepareOrderItemListForUpdate(this.order.purchaseOrderItems)
      }

      // Update purchase order
      this.$apiService.purchasingService.updatePurchaseOrder(this.$route.params.id, updateData)
      .then(response => {
        this.itemList.editing = false
        this.successToast('Purchase Order Updated')
        this.reloadData()
      })
      .catch(err => {
        this.itemList.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.itemList.submitting = false
      })
    },
    cancelEditingItemList() {
      this.itemList.editing = false
      this.itemList.form = this.clearFormResponse()
      this.reloadData()
    },
    updateSupplierConfirmation() {
      this.displayModal(SupplierConfirmationDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id)
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Updated')
          this.reloadData()
        }
      })
    },
    updateSupplierShipped() {
      this.displayModal(SupplierShippedDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
        isBulk: this.isTypeBulk
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Updated')
          this.reloadData()
        }
      })
    },
    updateDateAdvised() {
      this.displayModal(DateAdvisedDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
        isBulk: this.isTypeBulk
      })
      .then((success) => {
        if(success) {
          this.successToast('Advised Date Updated')
          this.reloadData()
        }
      })
    },
    updateDateAdvisedShipping() {
      this.displayModal(DateAdvisedDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id),
        isBulk: this.isTypeBulk,
        isShipping: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Advised Shipping Date Updated')
          this.reloadData()
        }
      })
    },
    editReferences() {
      this.displayModal(EditPurchaseOrderReferenceDialog, { 
        purchaseOrderId: parseInt(this.$route.params.id)
      }, this.order)
      .then((success) => {
        if(success) {
          this.successToast('Reference info updated')
          this.reloadData()
        }
      })

    },
    updateShipmentReceived() {
      this.confirmMessage('Register Shipment Received?', 'Are you sure you want to register the shipment as received and prepare the order for receiving?')
      .then(success => {
        if(success) {
          this.$apiService.purchasingService.shipmentReceivedPurchaseOrder(parseInt(this.$route.params.id))
          .then(response => {
            this.successToast('Shipment Received')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error moving to received shipment')
          })

        }
      })
    },
    updateGoodsReceived() {
      this.confirmMessage('Register Goods Received?', 'Are you sure you want to register the goods as received and prepare the order for receiving?')
      .then(success => {
        if(success) {
          this.$apiService.purchasingService.shipmentReceivedPurchaseOrder(parseInt(this.$route.params.id))
          .then(response => {
            this.successToast('Goods Received')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error moving to received goods')
          })

        }
      })
    },
    updateReadyForPickup() {
      this.confirmMessage('Goods available for pickup?', 'If the goods are ready for pickup, this will move the purchase order into pending pickup. Continue?')
      .then(success => {
        if(success) {
          this.$apiService.purchasingService.pickupReadyPurchaseOrder(parseInt(this.$route.params.id))
          .then(response => {
            this.successToast('Purchase order updated')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error moving to pending pickup')
          })

        }
      })
    },
    displayReceivingTab() {
      this.$refs.receivingTab.show()
    },
    displayPurchaseOrderReport(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/purchasing/purchaseorders/${this.$route.params.id}/report?${params}`)
    },
    displayReceivingSheet() {
      this.displayReportPDF(`/purchasing/purchaseorders/${this.$route.params.id}/receivesheetreport`)
    },
    displayReceivingLabels(filterItemId = null) {
      var printParams = { download: this.isDesktopAppMode(), label: true, blocklabel: true }

      var labelURL = `/purchasing/purchaseorders/${this.$route.params.id}/receiving/labels`
      if(filterItemId != null)
        labelURL = `/purchasing/purchaseorders/${this.$route.params.id}/receiving/labels/${filterItemId}`

      var params = $.param(printParams)
      this.$apiService.reportingService.generateReportSignedURL(`${labelURL}?${params}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    displayProductionPanelLabels(filterItemId = null) {
      var printParams = { download: this.isDesktopAppMode(), label: true }

      var labelURL = `/purchasing/purchaseorders/${this.$route.params.id}/receiving/labels`
      if(filterItemId != null)
        labelURL = `/purchasing/purchaseorders/${this.$route.params.id}/receiving/labels/${filterItemId}`

      var params = $.param(printParams)
      this.$apiService.reportingService.generateReportSignedURL(`${labelURL}?${params}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    calculateSubTotalPrice() {
      var totalPrice = 0
      if(this.order.purchaseOrderItems != null) {
        this.order.purchaseOrderItems.forEach(item => {
          totalPrice += Utils.roundCurrency(item.totalUnits * item.unitPrice)
        })
      }
      return totalPrice
    },
    calculateTaxPrice() {
      var totalTaxPrice = currency(0, { precision: 4 })
      if(this.order.purchaseOrderItems != null) {
        this.order.purchaseOrderItems.forEach(item => {

          let itemPrice = Utils.roundCurrency(item.totalUnits * item.unitPrice)
          if(item.taxRate)
            totalTaxPrice = totalTaxPrice.add(currency(item.taxRate, { precision: 4 }).multiply(0.01).multiply(itemPrice))
        })
      }
      return totalTaxPrice.value
    },
    calculateTotalPrice() {
      return this.calculateSubTotalPrice() + this.calculateTaxPrice()
    },

    /*
     PO Receiving
     */
    beginReceiveEditing() {
      this.editingReceiving = true
    },
    cancelReceiveEditing() {
      this.editingReceiving = false
      this.reloadData()
    },
    submitUpdateGoodsReceived() {

      let updateData = {
        goodsReceived: this.recvItemList,
        additionalCosts: this.order.additionalCosts
      }

      this.recvItemForm = {}
      this.submittingReceiving = true
      this.$apiService.purchasingService.updatePurchaseOrderGoodsReceived(parseInt(this.$route.params.id), updateData)
      .then(response => {
        this.successToast('Received List Updated')
        this.editingReceiving = false
        this.reloadData()
      })
      .catch(err => {
        this.$data.recvItemForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.submittingReceiving = false
      })
    },
    completeReceiveGoods() {
      // Popup a dialog to confirm details & for goods received (enter in notes)
      this.displayModal(GoodsReceivedDialog, { 
        purchaseOrder: this.order,
        purchaseOrderId: parseInt(this.$route.params.id),
        goodsReceivedList: this.recvItemList
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Processed & Completed')
          this.reloadData()
        }
      })   
    },

    // Received Items calculations
    recvNewAdditionalCost() {
      this.order.additionalCosts.push({
        name: 'None', cost: 0.0
      })
    },
    recvRemoveAdditionalCost(index) {
      this.order.additionalCosts.splice(index, 1)
    },
    calculateRecvTotalAdditionalCosts() {
      if(this.order.additionalCosts == null)
        return 0
      return Utils.roundCurrency(this.order.additionalCosts.reduce((cost, item) => {
        return cost + Utils.roundCurrency(item.cost)
      }, 0))
    },

    
    
    getAddCostListPromise() {
      return new Promise((resolve, reject) => {
        return this.$apiService.adminService.getDescriptorLookupListForType(DescriptorLookupTypes.poAdditionalCost)
        .then(response => {
          var output = [
            { value: 'None', name: 'None' }
          ]
          output = _.concat(output, response.data.map(lookup => {
            return { value: lookup.description, name: lookup.description }
          }))
          resolve(output)
        })
        .catch(() => { reject() })
      })
    },
    onBeforeUnload: function(event) {
      // Ignore for local dev
      if(this.$glass.isLocalSite)
        return null
      if(!this.editingReceiving)
        return null

      var confirmationMessage = `You have unsaved changes with receiving. Are you sure you want to exit?`

      event.returnValue = confirmationMessage
      return confirmationMessage
    },
  }
}

</script>